import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useDeepCompareEffect from 'use-deep-compare-effect'

import Spinner from '../../../App/Components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../App/Hooks/reduxHooks'
import { IPermission } from '../Client/IPermission'
import {
  getUserPermissions,
  updatePermissions as dispatchUpdatePermissions,
} from '../Store/Actions'
import Toggle from '../Toggle/Toggle'

import { IGroupedPermission, IStatus } from './IGroupedPermission'
import { IProps } from './IProps'
import { Button } from '../../../Common/Button'
import style from './List.scss'

const List = ({ userId }: IProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const canLoadPermissions = useAppSelector(
    state => !state.userPermissions.isLoading && !state.userPermissions.error,
  )

  const reduxPermissions: IPermission[] = useAppSelector(state => {
    if (typeof state.userPermissions.userPermissions === 'undefined') {
      return []
    }

    const permissionsState = state.userPermissions.userPermissions.filter(
      (userPermission: IPermission) => userPermission.userId === userId,
    )

    if (0 === permissionsState.length) {
      return []
    }

    return permissionsState
  })

  const [groupedPermissions, setGroupedPermissions] = useState<IGroupedPermission[]>([])
  const [flatPermissions, setFlatPermissions] = useState<IPermission[]>([])

  const establishment = useAppSelector(state => state.establishment.active)

  useDeepCompareEffect(() => {
    setFlatPermissions(reduxPermissions);
    if (reduxPermissions.length === 0) {
      return
    }

    const transformGroupedPermission = (
      permission: IPermission,
    ): IGroupedPermission => {
      return {
        ...permission,
        isCollapsed: true,
        status: permission.active ? 'active' : 'inactive',
        persisted: true,
      }
    }

    let groupedPermission: IGroupedPermission = {
      name: 'is_manager',
      active: false,
      userId,
      status: 'inactive',
      isCollapsed: false,
      persisted: false,
      permissions: [
        {
          name: 'customer',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter((permission: IPermission) => {
              if (
                permission.name === 'report.revenue_customer' ||
                permission.name === 'report.top_customers'
              ) {
                return false
              }

              return permission.name.toLowerCase().includes('customer')
            })
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
        {
          name: 'calendar',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter((permission: IPermission) => {
              if (permission.name === 'calendar.appointments.deleted.view') {
                return establishment?.preferences?.calendar_trash
              }

              if (permission.name.toLowerCase().includes('calendar')) {
                return true
              }

              return permission.name === 'is_visible_online'
            })
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
        {
          name: 'product',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter(
              (permission: IPermission) =>
                permission.name.toLowerCase().includes('product') ||
                permission.name === 'edit_stock',
            )
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
        {
          name: 'register',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter((permission: IPermission) => {
              if (
                permission.name === 'access_register_logbook' ||
                permission.name === 'checkRegister'
              ) {
                return true
              }

              return (
                permission.name.toLowerCase().includes('register.') &&
                !permission.name.toLowerCase().includes('settings')
              )
            })
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
        {
          name: 'report',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter((permission: IPermission) =>
              permission.name.toLowerCase().includes('report'),
            )
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
        {
          name: 'hr',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter((permission: IPermission) =>
              permission.name.toLowerCase().startsWith('hr.'),
            )
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
        {
          name: 'marketing',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter((permission: IPermission) =>
              permission.name.toLowerCase().startsWith('marketing.'),
            )
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
        {
          name: 'settings',
          active: false,
          userId,
          status: 'inactive',
          isCollapsed: true,
          persisted: false,
          permissions: reduxPermissions
            .filter((permission: IPermission) =>
              permission.name.toLowerCase().startsWith('settings'),
            )
            .map((permission: IPermission) =>
              transformGroupedPermission(permission),
            ),
        },
      ],
    }
    groupedPermission = setPartialPermission(groupedPermission)
    setGroupedPermissions([groupedPermission])
  }, [reduxPermissions, establishment])

  if (reduxPermissions.length === 0) {
    if (canLoadPermissions) {
      dispatch(getUserPermissions(userId))
    }

    return <Spinner />
  }

  const setPartialPermission = (permission: IGroupedPermission): any => {
    if (!permission.permissions || permission.permissions.length === 0) {
      return permission
    }

    for (const subPermissionIndex in permission.permissions) {
      permission.permissions[subPermissionIndex] = setPartialPermission(
        permission.permissions[subPermissionIndex],
      )
    }

    const statuses = getStatuses(permission.permissions)

    let isActive: boolean | undefined
    if (1 === statuses.length) {
      if (permission.persisted && permission.status !== statuses[0]) {
        isActive = statuses[0] === 'active'
      }
      permission.status = statuses[0]
    } else if (1 < statuses.length) {
      if (permission.persisted && permission.status === 'active')
        isActive = false
      permission.status = 'partial'
    }

    if (typeof isActive !== 'undefined') {
      const newPermission = {
        ...permission,
        active: isActive,
        status: isActive ? 'active' : 'inactive',
      }
    }

    return permission
  }

  const savePermissions = async () => {
    setIsSubmitting(true)
    const updatedPermissions = flatPermissions;
  
    try {
      await dispatch(dispatchUpdatePermissions(updatedPermissions))
    } catch (error) {
      console.error('Error updating permissions: ', error)
    }

    window.location.reload()
  }

  const updatePermission = (permission: IGroupedPermission, newStatus: IStatus) => {
    const active = newStatus === 'active'
    let newPermissions: IPermission[] = []

    if (permission.persisted) {
      newPermissions = [{ ...permission, active }]
    }

    if (permission.permissions && newStatus !== 'partial') {
      newPermissions = [
        ...newPermissions,
        ...updatePermissions(permission.permissions, newStatus),
      ]
    }

    const updateFlatPermission = (currentPermissions: IPermission[], updatedPermission: IPermission) => {
      return currentPermissions.map(permission => {
        if (permission.name === updatedPermission.name) {
          return {
            ...permission,
            ...updatedPermission
          };
        } else {
          return permission;
        }
      });
    };
    
    let newFlatPermissions: IPermission[] = flatPermissions;
    newPermissions.forEach((newPermission: IPermission) => {
      const updatedFlatPermissions = updateFlatPermission(newFlatPermissions, newPermission);
      newFlatPermissions = updatedFlatPermissions;
    });

    setFlatPermissions(newFlatPermissions);
  }

  const updatePermissions = (
    permissions: IGroupedPermission[],
    newStatus: IStatus,
  ): any => {
    const active = newStatus === 'active'
    let newPermissions: IGroupedPermission[] = []
    for (const subPermission of permissions) {
      if (subPermission.persisted) {
        newPermissions.push({
          ...subPermission,
          active,
          status: newStatus,
        })
      }

      if (subPermission.permissions) {
        newPermissions = [
          ...newPermissions,
          ...updatePermissions(subPermission.permissions, newStatus),
        ]
      }
    }

    return newPermissions
  }

  const findParent = (
    permission: IGroupedPermission,
    permissions: IGroupedPermission[],
  ): IGroupedPermission | undefined => {
    for (const parentPermission of permissions) {
      if (!parentPermission.permissions) {
        continue
      }

      for (const subPermission of parentPermission.permissions) {
        if (subPermission.name === permission.name) {
          return parentPermission
        }
      }

      const subParentPermission = findParent(
        permission,
        parentPermission.permissions,
      )
      if (subParentPermission) {
        return subParentPermission
      }
    }
  }

  const getStatuses = (subPermissions: IGroupedPermission[]): IStatus[] => {
    const statuses: IStatus[] = []
    for (const permission of subPermissions) {
      statuses.push(permission.status)
      if (permission.permissions) {
        statuses.concat(getStatuses(permission.permissions))
      }
    }

    return [...new Set(statuses)]
  }

  const isAllActive = (
    subPermissions: IGroupedPermission[],
    excludePermission: IGroupedPermission,
  ): boolean => {
    for (const permission of subPermissions) {
      if (permission.name === excludePermission.name) {
        continue
      }

      if (permission.active === false) {
        return false
      }

      if (permission.permissions) {
        if (!isAllActive(permission.permissions, excludePermission)) {
          return false
        }
      }
    }

    return true
  }

  const getPermissions = (userPermissions: IGroupedPermission[]): any => {
    return userPermissions.map((permission: IGroupedPermission) => {
      return (
        <Toggle
          label={t(`permission.${permission.name}`)}
          status={permission.status}
          isCollapsed={permission.isCollapsed}
          onChange={(status: IStatus) => updatePermission(permission, status)}
          key={`permission${permission.name}`}>
          {permission.permissions
            ? getPermissions(permission.permissions)
            : null}
        </Toggle>
      )
    })
  }

  return (
    <div>
      <>
        {isSubmitting && <Spinner />}
        
        {!isSubmitting &&
          <>
            {getPermissions(groupedPermissions)}

            <div className={`${style.buttonWrapper}`}>
              <Button onClick={() => savePermissions()}>
                {t('Save')}
              </Button>
            </div>
          </>
        }
      </>
    </div>)
}

export default List
